import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
// routes
import { paths } from 'src/routes/paths';
import { useRouter, useParams } from 'src/routes/hook';
// assets
import { countries } from 'src/assets/data';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// components
import { enqueueSnackbar, useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFTextField, RHFAutocomplete, RHFSelect } from 'src/components/hook-form';
import {
  endpoints,
  getFetcher1,
  getFetcherPrams,
  postFetcher,
  postFetcher1,
  postPramsFetcher1,
  putFetcher1,
} from 'src/utils/axios';
import 'react-phone-number-input/style.css';
import TourGuideAsignListUI from './tour-guide-asign-list-ui';

// ----------------------------------------------------------------------
// Form validation schema
const TourGuideSchema = Yup.object().shape({
  schema: Yup.object().nullable().required('Schema is required'),
  supplier: Yup.object().nullable().required('Supplier is required'),
});
export default function TourGuideUserMappingEditForm({ currentUser, isEditMode }) {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [tableData, setTableData] = useState([]);
  const [schemaList, setSchemaList] = useState([]);
  const [supplierList, setSupplierList] = useState([]);
  const [category, setCategory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [updateId, setUpdateId] = useState(null);
  const [isEnabled, setIsEnabled] = useState(true);
  const [schemaName, setSchemaName] = useState('');
  const [supplier, setSupplier] = useState('');
  const [items, setItems] = useState('');
  const [itemList, setItemList] = useState([]);
  const [itemName, setItemName] = useState('');
  const [categoryLevel, setCategoryLevel] = useState(0);
  const [toolbarSchemaId, setToolbarSchemaId] = useState(0);
  const [toolbarSupplierId, setToolbarSupplierId] = useState(0);
  const [search, setSearch] = useState('');

  console.log('tableData', tableData);

  // Default values for the form
  const defaultValues = useMemo(
    () => ({
      type: 1,
      discountMethod: 1,
      isEnabled,
      categoryLevel: 0,
      // isEnabled,
      // categoryLevel: currentUser?.categoryLevel ?? 0,
    }),
    [isEnabled]
  );

  // React Hook Form methods
  const methods = useForm({
    resolver: yupResolver(TourGuideSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  // Simulate loading state
  const loadingSend = useBoolean(false);
  // fetch api
  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };
      setTableLoading(true);
      console.log(
        'Fetching lines with search:',
        search,
        'toolbarSchemaId:',
        toolbarSchemaId?.id,
        'sup:',
        toolbarSupplierId?.id,
        'page:',
        page,
        'size:',
        size
      );

      const param = {
        name: search === null ? '' : search || '',
        schemaId: toolbarSchemaId?.id || 0,
        tourGuideId: toolbarSupplierId?.id || 0,
      };
      const response = await postPramsFetcher1(
        `${endpoints.tourGuide.mappingSearch}/${page}/${size}/${0}`,
        null,
        param,
        config
      );
      if (!response || (response.status !== 201 && response.status !== 200)) {
        setTableData(null);

        throw new Error(response?.message || 'Failed to fetch lines');
      }
      console.log('Response from fetchLineItems:', response);

      setTableData(response?.data);
    } catch (error) {
      setTableData(null);
      console.error('Error fetching schemas:', error);
    }
    setTableLoading(false);
    setLoading(false);
  }, [search, toolbarSchemaId, toolbarSupplierId, page, size]);

  // create api call
  const handleCreateAndSend = handleSubmit(async (data) => {
    console.log('handleCreateAndSend called with data:', data);
    loadingSend.onTrue();
    // @RequestBody List < TourGuideSchemaLineItem > schema

    const payload = {
      tourGuideId: methods.getValues('supplier')?.id || null,
      tourGuideSchemaId: methods.getValues('schema')?.id || null,
      isEnabled,
    };

    console.log('Payload for API call:', payload);

    try {
      const response =
        updateId === null
          ? await postFetcher1(endpoints.tourGuide.mappingCreate, payload)
          : await putFetcher1(
              `${endpoints.tourGuide.createTourGuideLineItem}/${updateId}`,
              payload
            );
      await fetchList();
      if (!response || (response.status !== 201 && response.status !== 200)) {
        enqueueSnackbar('Failed to create tour guide schema', {
          variant: 'error',
          autoHideDuration: 5000,
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          sx: {
            width: '400px',
          },
        });

        setIsEnabled(true);
        throw new Error(response?.message || 'Failed to create tour guide schema');
      } else {
        enqueueSnackbar('Tour guide schema created successfully', {
          variant: 'success',
          autoHideDuration: 5000,
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          sx: {
            width: '400px',
          },
        });
        // Reset form if needed
        methods.reset({
          id: updateId || null,
          configurationName: '',
          schema: null,
          type: 1,
          discountMethod: 1,
          amount: '',
          categoryLevel: 0,
          categoryId: null,
          isEnabled,
        });

        methods.reset();
      }

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      // Show success message or handle response
    } catch (error) {
      console.log('Error creating tour guide schema:', error);

      if (error?.status === 406) {
        enqueueSnackbar(error?.data, {
          variant: 'error',
          autoHideDuration: 5000,
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          sx: {
            width: '400px',
          },
        });
      } else {
        enqueueSnackbar('Failed to create tour guide schema', {
          variant: 'error',
          autoHideDuration: 5000,
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          sx: {
            width: '400px',
          },
        });
      }
    } finally {
      loadingSend.onFalse();
    }
    setUpdateId(null);
  });

  const setToolbarSchemaIdFromChild = (id, name, supplierId) => {
    console.log('setToolbarSchemaIdFromChild called with id:', id, ' name:', name);
    setToolbarSchemaId(id);
    setToolbarSupplierId(supplierId);
    setSearch(name);
  };

  const fetchTourGuideSchemas = useCallback(async () => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };
      const param = {
        name: schemaName || '',
      };
      const response = await postPramsFetcher1(
        `${endpoints.tourGuide.search}/${0}/${10}/${0}`,
        null,
        param,
        config
      );

      console.log('Response from fetchTourGuideSchemas:', response?.data?.content);

      if (response && response.status === 200) {
        setSchemaList(response?.data?.content || []);
      }
    } catch (error) {
      console.log('Error fetching tour guide schemas:', error);
    }
  }, [schemaName]);

  const fetchSupplier = useCallback(async () => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };
      const param = {
        name: supplier || '',
        type: 2,
      };
      const response = await postPramsFetcher1(
        `${endpoints.supplier.supplierSearchBySupplierType}/${0}/${10}/1`,
        null,
        param,
        config
      );

      console.log('Response from supplier:', response?.data?.content);

      if (response && response.status === 200) {
        setSupplierList(response?.data?.content || []);
      }
    } catch (error) {
      console.log('Error fetching tour guide schemas:', error);
    }
  }, [supplier]);

  const handleEditRowEnable = useCallback(
    async (row, isEnabled2) => {
      setLoading(true);
      console.log('rowFire', row);
      let isEnabled1;
      if (isEnabled2 === true || isEnabled2 === 'true' || isEnabled2 === '1') {
        isEnabled1 = false;
      } else {
        isEnabled1 = true;
      }

      try {
        const payload = {
          ...row,
          isEnabled: isEnabled1,
        };
        const response = await putFetcher1(
          `${endpoints.tourGuide.mappingCreate}/${row.id}`,
          payload
        );
        if (!response || response.status !== 200) {
          enqueueSnackbar('Failed to update tour guide schema', {
            variant: 'error',
            autoHideDuration: 5000,
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
            sx: { width: '400px' },
          });
        } else {
          enqueueSnackbar('Tour guide schema updated successfully', {
            variant: 'success',
            autoHideDuration: 5000,
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
            sx: { width: '400px' },
          });

          fetchTourGuideSchemas();
        }
      } catch (error) {
        enqueueSnackbar('Failed to update tour guide schema', {
          variant: 'error',
          autoHideDuration: 5000,
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          sx: { width: '400px' },
        });
      }

      await fetchList();
    },
    [fetchTourGuideSchemas, fetchList]
  );

  useEffect(() => {
    function fetchTourGuideSchemas1() {
      fetchTourGuideSchemas();
    }
    fetchTourGuideSchemas1();
  }, [fetchTourGuideSchemas]);
  useEffect(() => {
    function fetchSupplier1() {
      fetchSupplier();
    }
    fetchSupplier1();
  }, [fetchSupplier]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Handler to set the page size
  const handleSizeChange = (newSize) => {
    setSize(newSize);
    // setPage(0); // Optionally reset to first page when size changes
  };

  const handleEditRow = useCallback(
    async (row) => {
      try {
        // Push row?.schemaList to schemaList if not already present (by id)
        if (row?.tourGuideSchema && !schemaList.some((s) => s.id === row.schemaList.id)) {
          setSchemaList((prev) => [...prev, row.tourGuideSchema]);
        }
      } catch (error) {
        console.log('Error adding category:', error);
      }

      console.log('Editing row:', row);
      methods.setValue('schema', row?.schema || null);
      setIsEnabled(row?.isEnabled);

      setUpdateId(row?.id || null);
      if (row?.type === 1) {
        setCategoryLevel(row?.categoryLevel || 0);
      }
    },
    [methods, schemaList]
  );

  console.log('Fetching schemas with page:', page, 'size:', size);

  useEffect(() => {
    fetchList();
  }, [page, size, toolbarSchemaId, search, fetchList]);

  const setNameFromChild = (name) => {
    // setName(name);
  };
  return (
    <FormProvider methods={methods}>
      <Grid container spacing={3}>
        <Grid xs={12} md={12}>
          <Card sx={{ p: 3 }}>
            <Typography sx={{ mb: 1, fontWeight: 'bold' }}>Assign Tour Guide to Schema</Typography>
            <br />
            <Box
              rowGap={3}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
              }}
              sx={{ mb: 2 }}
            >
              {/* <RHFTextField name="configurationName" label="Configuration Name" size="small" value={methods.watch('configurationName') || ''} /> */}
              <RHFAutocomplete
                name="schema"
                label="Schema"
                size="small"
                options={schemaList}
                value={methods.watch('schema') || null}
                getOptionLabel={(option) => option.name || ''}
                onInputChange={(_, value) => {
                  setSchemaName(value);
                }}
                onChange={(_, value) => {
                  methods.setValue('schema', value || null);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option?.name || '-'}
                  </li>
                )}
              />

              <RHFAutocomplete
                name="supplier"
                label="Supplier"
                size="small"
                options={supplierList}
                value={methods.watch('supplier') || null}
                getOptionLabel={(option) => `${option?.suplier_code} - ${option?.name}` || ''}
                onInputChange={(_, value) => {
                  setSupplier(value);
                }}
                onChange={(_, value) => {
                  methods.setValue('supplier', value || null);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {`${option?.suplier_code} - ${option?.name}` || '-'}
                  </li>
                )}
              />

              {/* <RHFAutocomplete
                name="items"
                label="Items"
                size="small"
                multiple
                freeSolo={false}
                options={itemList}
                value={methods.watch('items') || []}
                getOptionLabel={(option) => option.name || ''}
                onInputChange={(_, value) => {
                  setItemName(value);
                }}
                onChange={(_, value) => {
                  console.log('Input changed:', value);

                  methods.setValue('items', value || []);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.id}>
                    <input type="checkbox" checked={selected} style={{ marginRight: 8 }} readOnly />
                    {option?.name || '-'}
                  </li>
                )}
              /> */}
            </Box>
            <Stack justifyContent="flex-end" direction="row" spacing={2} sx={{ mt: 2 }}>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={loadingSend.value && isSubmitting}
                onClick={handleCreateAndSend}
                size="small"
              >
                {updateId === null ? 'Create Tour Guide Schema' : 'Update Tour Guide Schema'}
              </LoadingButton>
            </Stack>
          </Card>
          <br />
          <br />
          <TourGuideAsignListUI
            tableLoading={tableLoading}
            handlePageChange={handlePageChange}
            handleSizeChange={handleSizeChange}
            tableDataFromParent={tableData}
            handleEditRowParent={handleEditRow}
            handleEditRowEnable={handleEditRowEnable}
            setNameFromChild={setNameFromChild}
            loading={loading}
            setToolbarSchemaIdFromChild={setToolbarSchemaIdFromChild}
          />
        </Grid>
      </Grid>
    </FormProvider>
  );
}

TourGuideUserMappingEditForm.propTypes = {
  currentUser: PropTypes.object,
  isEditMode: PropTypes.bool,
};
